package com.flowyride.feature.planner

import com.flowyride.core.domain.analysis.Curvature
import com.flowyride.core.domain.favorite.FavoriteSummary
import com.flowyride.core.domain.gpx.GpxDocument
import com.flowyride.core.domain.gpx.GpxWaypoint
import com.flowyride.core.domain.loop.CompassDirection
import com.flowyride.core.domain.loop.LoopRequest
import com.flowyride.core.domain.loop.LoopTarget
import com.flowyride.core.domain.model.GeoPoint
import com.flowyride.core.domain.model.Level
import com.flowyride.core.domain.model.Place
import com.flowyride.core.domain.model.Route
import com.flowyride.core.domain.model.RouteEngineId
import com.flowyride.core.domain.model.RoutePreferences
import com.flowyride.core.domain.place.SavedPlace
import com.flowyride.core.domain.speedcamera.SpeedCamera
import com.flowyride.core.domain.usecase.GpxContent
import com.flowyride.core.domain.usecase.ImportedTrack
import org.jetbrains.compose.resources.StringResource
import kotlin.jvm.JvmInline

/** Stable identity of a stop, kept across reorderings (Compose keys, async search results). */
@JvmInline
value class StopId(val value: Long)

/** Role derived from the position in the list: first = departure, last = arrival. */
enum class StopRole { DEPARTURE, INTERMEDIATE, ARRIVAL }

/** What a point picked on the map becomes. */
enum class MapPickTarget {
    /** First empty field, in riding order. */
    NEXT_EMPTY,
    DEPARTURE,

    /** New intermediate stop, inserted where it makes the smallest detour. */
    STOP,
    ARRIVAL,
}

/** A stop the current route actually goes through. */
data class RoutedStop(val id: StopId, val place: Place)

data class StopUiState(
    val id: StopId,
    val query: String = "",
    val selected: Place? = null,
    val suggestions: List<Place> = emptyList(),
    val isSearching: Boolean = false,
    /** "Ma position": set from the rider's location, moved there again when the navigation starts. */
    val followsRider: Boolean = false,
)

data class EngineUiModel(
    val id: RouteEngineId,
    val name: String,
    val isAvailable: Boolean,
    val supportsPreferences: Boolean,
    val maxWaypoints: Int,
)

sealed interface RouteUiState {
    data object Idle : RouteUiState
    data object Loading : RouteUiState
    data class Ready(
        val route: Route,
        val engineName: String,
        /** Null when the route is too short to be scored. */
        val curvature: Curvature?,
        /** Stops the route goes through, in order (empty stops excluded): what the GPX exports. */
        val stops: List<RoutedStop>,
        /** Official fixed cameras standing on the route. */
        val speedCamerasOnRoute: Int = 0,
        /** A line read from a GPX file: shown as is, no engine involved. */
        val isImported: Boolean = false,
        /** The favorite this very route is saved as; null until saved. */
        val savedAs: FavoriteSummary? = null,
        /** The speed limits of a line without them (imported, older favorite) are being fetched. */
        val isFetchingSpeedLimits: Boolean = false,
    ) : RouteUiState

    /** @param details Raw engine message, useful for debugging. */
    data class Failed(val reason: StringResource, val details: String?) : RouteUiState
}

/** Route: the rider places every stop. Loop: the rider gives a departure and a length, stops are generated. */
enum class PlannerMode { ROUTE, LOOP }

enum class LoopTargetKind { DISTANCE, DURATION }

data class LoopUiState(
    val targetKind: LoopTargetKind = LoopTargetKind.DISTANCE,
    val distanceKm: Int = 150,
    val durationMinutes: Int = 180,
    /** Null: any direction. */
    val direction: CompassDirection? = null,
    val isGenerating: Boolean = false,
    /** Last generated loop's request: "another loop" replays it with another seed. */
    val lastRequest: LoopRequest? = null,
) {
    val target: LoopTarget
        get() = when (targetKind) {
            LoopTargetKind.DISTANCE -> LoopTarget.Distance(distanceKm * 1_000.0)
            LoopTargetKind.DURATION -> LoopTarget.Duration(durationMinutes * 60.0)
        }

    companion object {
        val DISTANCE_KM = 20..600
        val DURATION_MINUTES = 30..600
    }
}

/** A line imported from a GPX file: read-only, the stops panel shows its name instead of the fields. */
data class ImportedRideUiState(
    val name: String,
    /** The file's waypoints, shown on the map. */
    val waypoints: List<GpxWaypoint>,
)

data class FavoritesUiState(
    val items: List<FavoriteSummary> = emptyList(),
    val isLoading: Boolean = false,
    /** Loading the list failed. */
    val failed: Boolean = false,
    /** A favorite is being opened or saved. */
    val isBusy: Boolean = false,
)

/** An imported file needs the rider's answer before anything is shown. */
sealed interface GpxQuestion {
    /** Several lines in the file: which one? */
    data class ChooseTrack(val tracks: List<ImportedTrack>) : GpxQuestion

    /** Only points in the file: FlowyRide will compute the line between them. */
    data class ComputeFromPoints(val content: GpxContent.PointsOnly) : GpxQuestion
}

data class PlannerUiState(
    /** Departure first, arrival last, intermediate stops in between. Always at least 2. */
    val stops: List<StopUiState>,
    val mode: PlannerMode = PlannerMode.ROUTE,
    val loop: LoopUiState = LoopUiState(),
    val engines: List<EngineUiModel> = emptyList(),
    val selectedEngineId: RouteEngineId? = null,
    val preferences: RoutePreferences = RoutePreferences(),
    val route: RouteUiState = RouteUiState.Idle,
    /** Official fixed cameras (empty until loaded). */
    val speedCameras: List<SpeedCamera> = emptyList(),
    val showSpeedCameras: Boolean = true,
    val imported: ImportedRideUiState? = null,
    val gpxQuestion: GpxQuestion? = null,
    val favorites: FavoritesUiState = FavoritesUiState(),
    /** Changes whenever a whole new ride replaces the current one (file, favorite, loop): the map frames it. */
    val rideGeneration: Int = 0,
    /** The device can guide with its GPS; otherwise only the simulation is offered. */
    val canNavigate: Boolean = false,
    val speedCameraAlerts: Boolean = false,
    /** Where the rider is, once asked for ("my position" button). */
    val userLocation: GeoPoint? = null,
    val isLocating: Boolean = false,
    /** Where the "my position" button asked to centre: set only then, the map never follows the GPS on its own. */
    val locatedAt: GeoPoint? = null,
    /** Changes each time the map must centre on [locatedAt]. */
    val locateGeneration: Int = 0,
    /** Named addresses of the account, oldest first. */
    val savedPlaces: List<SavedPlace> = emptyList(),
    val history: RideHistory = RideHistory(),
) {
    /** Only the edits of the ride on screen, in route mode (an imported line is not editable). */
    private val historyApplies: Boolean
        get() = history.generation == rideGeneration && mode == PlannerMode.ROUTE && imported == null

    val canUndo: Boolean
        get() = historyApplies && history.undo.isNotEmpty()

    val canRedo: Boolean
        get() = historyApplies && history.redo.isNotEmpty()

    val selectedEngine: EngineUiModel?
        get() = engines.firstOrNull { it.id == selectedEngineId }

    val canAddStop: Boolean
        get() = stops.size < (selectedEngine?.maxWaypoints ?: Int.MAX_VALUE)

    fun roleOf(index: Int): StopRole = stopRole(index, stops.size)

    /** A loop starts from the departure field, shared with the route mode. */
    val canGenerateLoop: Boolean
        get() = stops.first().selected != null && !loop.isGenerating

    val hasEmptyStop: Boolean
        get() = stops.any { it.selected == null }

    /** Something would be lost by importing a file or opening a favorite. */
    val hasRide: Boolean
        get() = imported != null || stops.any { it.selected != null }

    /** Name proposed when saving the current route as a favorite. */
    val suggestedFavoriteName: String
        get() {
            imported?.let { return it.name }
            val stops = (route as? RouteUiState.Ready)?.stops ?: return ""
            val departure = stops.first().place.name
            val arrival = stops.last().place.name
            return if (departure == arrival) departure else "$departure → $arrival"
        }

    /** Departure and arrival can only be removed while there are intermediate stops to take their place. */
    val canRemoveStops: Boolean
        get() = stops.size > 2

    /**
     * Places to route through: empty intermediate stops are skipped so that adding a stop
     * does not wipe the current route. Null while departure or arrival is missing.
     */
    val routableStops: List<RoutedStop>?
        get() {
            if (stops.first().selected == null || stops.last().selected == null) return null
            return stops.mapNotNull { stop -> stop.selected?.let { RoutedStop(stop.id, it) } }
        }
}

fun stopRole(index: Int, stopCount: Int): StopRole = when (index) {
    0 -> StopRole.DEPARTURE
    stopCount - 1 -> StopRole.ARRIVAL
    else -> StopRole.INTERMEDIATE
}

sealed interface PlannerAction {
    data class QueryChanged(val stop: StopId, val query: String) : PlannerAction
    data class PlaceSelected(val stop: StopId, val place: Place) : PlannerAction
    data class FieldCleared(val stop: StopId) : PlannerAction
    /** Inserts an empty stop just before the arrival. */
    data object AddStop : PlannerAction
    data class RemoveStop(val stop: StopId) : PlannerAction
    data class MoveStop(val stop: StopId, val toIndex: Int) : PlannerAction
    data class MapPointPicked(val point: GeoPoint, val target: MapPickTarget) : PlannerAction
    /**
     * The route line was dragged from [trackIndex] and dropped at [point]:
     * adds a stop there, between the two stops of the grabbed leg.
     */
    data class TrackDragged(val trackIndex: Int, val point: GeoPoint) : PlannerAction
    /** A stop's marker was dragged to [point] on the map. */
    data class StopMovedOnMap(val stop: StopId, val point: GeoPoint) : PlannerAction
    data object ReverseStops : PlannerAction
    /** Ride there: [place] becomes the arrival. */
    data class DestinationChosen(val place: Place) : PlannerAction
    /** The empty departure becomes "Ma position" (the UI got the location permission first). */
    data object DepartureFromRider : PlannerAction
    data class SavePlace(val name: String, val place: Place) : PlannerAction
    /** Empties departure, stops and arrival: back to a blank route. */
    data object ClearStops : PlannerAction
    data class EngineSelected(val id: RouteEngineId) : PlannerAction
    data class WindingnessChanged(val level: Level) : PlannerAction
    data class AvoidMotorwaysChanged(val avoid: Boolean) : PlannerAction
    data class ShowSpeedCamerasChanged(val show: Boolean) : PlannerAction
    data object RetryRoute : PlannerAction
    data class ModeChanged(val mode: PlannerMode) : PlannerAction
    data class LoopTargetKindChanged(val kind: LoopTargetKind) : PlannerAction
    data class LoopDistanceChanged(val km: Int) : PlannerAction
    data class LoopDurationChanged(val minutes: Int) : PlannerAction
    data class LoopDirectionChanged(val direction: CompassDirection?) : PlannerAction
    /** Replaces the stops with a loop from the departure, then shows it in the route mode. */
    data object GenerateLoop : PlannerAction
    /** Same settings as the last loop, another shape. */
    data object RerollLoop : PlannerAction
    data object ExportGpx : PlannerAction

    /** A file the rider picked: replaces the current ride (the UI asked first). */
    data class ImportGpx(val fileName: String, val content: String) : PlannerAction
    /** A file opened from another app while a ride is shown: kept in the favorites, the ride stays. */
    data class SaveGpxToFavorites(val fileName: String, val content: String) : PlannerAction
    /** Asks the server for the speed limits along a line that has none, to color it. */
    data object FetchSpeedLimits : PlannerAction
    /** Back to the ride before the last edit, route included; [Redo] goes forward again. */
    data object Undo : PlannerAction
    data object Redo : PlannerAction
    data class GpxTrackChosen(val track: ImportedTrack) : PlannerAction
    data object GpxPointsConfirmed : PlannerAction
    data object GpxQuestionDismissed : PlannerAction
    /** Leaves the imported line for a new, empty route. */
    data object CloseImported : PlannerAction

    data object LoadFavorites : PlannerAction
    /** Replaces the current ride with a saved one (the UI asked first). */
    data class OpenFavorite(val id: Long) : PlannerAction
    data class SaveFavorite(val name: String) : PlannerAction
    data class RenameFavorite(val id: Long, val name: String) : PlannerAction
    data class DeleteFavorite(val id: Long) : PlannerAction

    /** Starts the navigation along the route on screen; [simulated] rides it without GPS. */
    data class StartNavigation(val simulated: Boolean) : PlannerAction
    data class SpeedCameraAlertsChanged(val enabled: Boolean) : PlannerAction

    /** Centres the map on the rider (the UI got the location permission first). */
    data object LocateMe : PlannerAction

    /** The planner is on screen ([active]) or not: the position is followed only meanwhile. */
    data class LocationTrackingChanged(val active: Boolean) : PlannerAction
}

/** One-shot effects the UI must perform. */
sealed interface PlannerEvent {
    data class SaveGpx(val document: GpxDocument) : PlannerEvent
    data class ShowError(val message: StringResource) : PlannerEvent
    data class ShowMessage(val message: StringResource) : PlannerEvent
    /** A favorite was opened: the favorites list can close. */
    data object FavoriteOpened : PlannerEvent
}
